<script>
import {reactive, computed, Transition, defineComponent} from 'vue';
import Icon from '../../components/Icon.vue';

export default defineComponent({
  name: 'AccordionItem',
  components: {
    Transition,
    Icon,
  },
  props: {
    /**
     * The AccordionItem will be open initially if set to true.
     */
    startOpen: {
      type: Boolean,
      default: false,
    },
    /**
     * Whether the accordion toggles open/closed on click.
     */
    clickToToggle: {
      type: Boolean,
      default: false,
    },

    noBorder: {
      type: Boolean,
      default: false
    },

    noPadding: {
      type: Boolean,
      default: false
    },

    noChevron: {
      type: Boolean,
      default: false
    },
    noShadow: {
      type: Boolean,
      default: false
    }
  },
  emits: ['itemToggled', 'headerClick'],
  setup(props) {
    props = reactive(props);
    return {
      classes: computed(() => ({})),
      style: computed(() => ({})),
    };
  },
  data() {
    return {
      show: this.startOpen,
    };
  },
  computed: {
    accordionClass() {
      return {
        'accordion-item': true,
        'accordion-item-no-border': this.noBorder,
        'accordion-item-no-padding': this.noPadding,
        'accordion-item-no-shadow': this.noShadow,
        'accordion--open': this.show,
        'accordion--closed': !this.show
      };
    },
  },
  methods: {
    toggle(show) {
      this.show = show !== undefined ? show : !this.show;
      this.$emit('itemToggled', this.show);
    },
    accordionClicked(event) {
      // Prevent closing if clicking inside an interactive form element
      if (event.target.closest("input, textarea, button, select")) {
        event.stopPropagation(); // Stop event from bubbling up
        return;
      }

      if (this.clickToToggle) {
        this.toggle();
        this.$emit("itemToggled", this.show);
      }
      this.$emit("headerClick", this.show);

    },
    expand() {
      this.show = true;
    },
    collapse() {
      this.show = false;
    },
    // Animation functions for smooth height transitions
    beforeEnter(el) {
      el.style.height = '0';
    },
    enter(el) {
      el.style.height = el.scrollHeight + 'px';
      setTimeout(() => {
        el.style.height = 'auto';
      }, 250);
    },
    beforeLeave(el) {
      el.style.height = el.scrollHeight + 'px';
    },
    leave(el) {
      el.style.height = '0';
    },
  },
});
</script>

<template>
  <div
    ref="accordionItem"
    :class="accordionClass"
  >
    <div
      class="accordion-header-container"
      @click="accordionClicked"
    >
      <div class="accordion-header-content">
        <!-- Title Slot -->
        <slot
          name="titleSlot"
          class="accordion-title"
        />
      </div>
      <!-- Chevron Icon -->
      <Icon
        v-if="!noChevron"
        icon="chevron-down"
        size="10px"
        :class="{ rotated: show }"
        class="accordion-chevron"
      />
    </div>
    <transition
      name="accordion"
      @before-enter="beforeEnter"
      @enter="enter"
      @before-leave="beforeLeave"
      @leave="leave"
    >
      <div
        v-show="show"
        class="accordion-content"
      >
        <!-- Body Slot -->
        <slot
          name="bodySlot"
          class="accordion-body-text"
        />
        <div
          v-if="$slots.default"
          class="accordion-slot-wrapper"
        >
          <slot />
        </div>
      </div>
    </transition>
  </div>
</template>

<style scoped lang="scss">
@import '@/styles/_variables.scss';

.accordion-item {
  background-color: transparent;
  border: 1px solid #e8e8e8 !important;
  border-radius: 0.5rem;
  box-shadow: 0 5px 10px -3px rgba(0, 0, 0, 0.1);
  overflow: hidden;

  &.accordion-item-no-shadow {
    box-shadow: none;
  }

  &.accordion-item-no-border {
    border: none !important;
    box-shadow: none;
  }

  &.accordion-item-no-padding {
    .accordion-header-container {
      padding: 0 0;
      margin: 0 !important;
    }
  }

}

/* Header Container styled like a card header */
.accordion-header-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 1.5rem;
  background-color: transparent;
  cursor: pointer;
  user-select: none;
  transition: background-color 0.3s ease;
}

/* Header Content */
.accordion-header-content {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.accordion-title {
  font-size: 1rem;
  font-weight: 600;
  color: #333;
  padding-left: 0;
}

/* Chevron Icon with rotation effect */
.accordion-chevron {
  transition: transform 0.3s ease;
}

.accordion-chevron.rotated {
  transform: rotate(180deg);
}

/* Accordion Content */
.accordion-content {
  padding: 0;
  background-color: transparent;
  overflow: hidden;
  transition: height 0.25s ease-out;

  .accordion-slot-wrapper {
    margin-top: 0.75rem;
  }
}

/* Transition Classes */
.accordion-enter-active,
.accordion-leave-active {
  transition: height 0.25s ease-out;
}

.accordion-enter-from,
.accordion-leave-to {
  height: 0;
}
</style>

